import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames";
import { useState } from "react";
import KuikaCloseButton from "../kuika-close-button/kuika-close-button";
import { markPopoverFocusRestore } from "../kuika-tooltip/tooltip-focus-guard";
import KuikaSmallBoldText from "../typography/SmallBoldText/SmallBoldText";
import "./kuika-popover.scss";

type PopoverPosition = "top" | "right" | "bottom" | "left";
type PopoverAlign = "start" | "center" | "end";
/** `inverse` is the canvas selection overlay's surface (--selection-bar-* tokens), themed light or dark with the designer. */
type PopoverTone = "default" | "inverse";

export interface IKuikaPopoverProps {
  children: React.ReactNode;
  content: React.ReactNode;
  hideCloseIcon?: boolean;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  title?: string | React.ReactNode;
  position?: PopoverPosition;
  align?: PopoverAlign;
  sideOffset?: number;
  /** Minimum gap (px) kept between the popover and the viewport edges so it
   *  never sits flush against them. Passed through to Radix; default keeps the
   *  existing flush behavior. */
  collisionPadding?: number;
  /** Element(s) the popover must stay inside, instead of the viewport — e.g. the canvas region for a
   *  popover opened from the canvas selection overlay. Passed through to Radix. */
  collisionBoundary?: Popover.PopoverContentProps["collisionBoundary"];
  /** Radix's flip/shift-to-fit behaviour; on by default. */
  avoidCollisions?: boolean;
  /** Surface colour set. Defaults to the themed popover surface. */
  tone?: PopoverTone;
  className?: string;
  preventCloseOnClickOutside?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onPointerDown?: (e: React.PointerEvent) => void;
  /** Forwarded to the popover surface. Hover-driven popovers must keep the whole
   *  surface (title + padding, not just the content) alive, or the pointer "dies"
   *  crossing the title bar on its way in. */
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  /** ARIA role of the popover surface, for a popover that is not a plain dialog — a confirmation
   *  asks for a decision and is an `alertdialog` (KuikaPopconfirm). Deliberately NOT destructured:
   *  Radix writes its own `role="dialog"` before spreading the caller's props, so a destructured
   *  `role={role}` would hand it `undefined` and erase that default on every popover that does not
   *  pass one. Riding the rest spread means the key only exists when a caller actually set it. */
  role?: string;
  /**
   * Radix's own Escape hook, forwarded through the rest spread below (deliberately not
   * destructured, for the same reason `role` isn't — see above). Call `event.preventDefault()` to
   * keep the popover open instead of letting Escape close it, e.g. a multi-step popover using
   * Escape to step back rather than dismiss. Omitted, Escape closes as usual.
   */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /**
   * Radix's open auto-focus hook, forwarded through the rest spread like `onEscapeKeyDown`. By default
   * Radix focuses the first tabbable element; a popover that knows its primary control (a search
   * field) calls `event.preventDefault()` and focuses that instead.
   */
  onOpenAutoFocus?: (event: Event) => void;
  /** Forwarded to the popover surface (Radix Content) through the rest spread, like the two hooks above. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onMouseDownCapture?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const KuikaPopover = ({
  children,
  content,
  visible: controlledVisible,
  onVisibleChange,
  title,
  position = "bottom",
  align = "center",
  sideOffset = 5,
  collisionPadding,
  collisionBoundary,
  avoidCollisions,
  tone = "default",
  hideCloseIcon,
  className,
  preventCloseOnClickOutside,
  ...props
}: IKuikaPopoverProps): JSX.Element => {
  const [uncontrolledVisible, setUncontrolledVisible] = useState(false);
  const isControlled = controlledVisible !== undefined;
  const visible = isControlled ? controlledVisible : uncontrolledVisible;

  const handleVisibilityChange = (nextVisible: boolean) => {
    if (!isControlled) setUncontrolledVisible(nextVisible);
    onVisibleChange?.(nextVisible);
  };

  return (
    <Popover.Root open={visible} onOpenChange={handleVisibilityChange}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={classNames({
            "kuika-popover": true,
            [`kuika-popover--${position}`]: true,
            "kuika-popover--inverse": tone === "inverse",
            [`${className}`]: !!className
          })}
          side={position}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          collisionBoundary={collisionBoundary}
          avoidCollisions={avoidCollisions}
          // Focus going back to the trigger on close must not open the trigger's tooltip.
          onCloseAutoFocus={markPopoverFocusRestore}
          onInteractOutside={(e) => {
            // Antd / rc-select / rc-picker dropdowns render to document.body
            // via their own portals. A click on those should NOT count as
            // "outside" — keep the popover open. Same for a KModal the popover
            // opened (its root is `.k-modal-root`: the antd `.ant-modal-root`
            // this used to name is no longer rendered, so a click in the modal
            // closed the popover under it), tooltips, and color pickers that may
            // live inside popover content.
            const target = e.target as Element | null;
            if (
              target &&
              (target.closest(".rc-select-dropdown") ||
                target.closest(".k-modal-root") ||
                target.closest(".ant-select-dropdown") ||
                target.closest(".ant-picker-dropdown") ||
                target.closest(".ant-cascader-dropdown") ||
                target.closest(".ant-cascader-menus") ||
                target.closest(".ant-tooltip") ||
                target.closest(".kuika-tooltip__content") ||
                target.closest(".kuika-dropdown__menu") ||
                target.closest(".kuika_colorPicker"))
            ) {
              e.preventDefault();
              return;
            }
            if (preventCloseOnClickOutside) {
              e.preventDefault();
            }
          }}
          {...props}
        >
          {title && (
            <div className="kuika-popover__title">
              <KuikaSmallBoldText>{title}</KuikaSmallBoldText>
              {!hideCloseIcon && (
                <Popover.Close asChild>
                  <KuikaCloseButton
                    className="kuika-popover__close"
                    data-testid="closePopoverButton"
                    data-onboarding-id="ui-kit_popover_close"
                  />
                </Popover.Close>
              )}
            </div>
          )}
          <div className="kuika-popover__body">{content}</div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default KuikaPopover;
